/*
 * fit-decode.js — a decoder for Garmin FIT files (activities, courses, dives),
 * written from the published FIT protocol. No Garmin SDK code is shipped: the
 * SDK's licence covers internal use, not redistribution, so it served only as
 * the reference oracle while this was written (2026-09-24: identical fields on
 * 21 of 23 real files; the SDK itself threw on the other two). The committed
 * test (tests/js/gps-track-fit.test.mjs) builds its files byte by byte from
 * the protocol instead, so it runs without the SDK.
 *
 *   GpsFitDecode.decode(u8) → { messages:[{g, f:{num:val}, d:{'idx:num':val}}],
 *                               dev:{'idx:num':{name, units, scale, offset, nativeMesg, nativeField}},
 *                               protocol, profile, crcOk, truncated, warnings:[] }
 *
 * Values are RAW (unscaled integers; strings decoded as UTF-8). Invalid values
 * (the base type's «no data» marker) are omitted, so `f[3] === undefined`
 * means «this record has no heart rate», never «heart rate 255».
 *
 * Robustness is the point, not an afterthought: a watch that ran out of
 * battery mid-activity writes a file with no trailing CRC and a header whose
 * data size is 0; Strava's «export original» of a merged activity is several
 * FIT files CHAINED back to back. Both decode here; both are real files in the
 * test corpus.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);

    var CRC_T = [0x0000, 0xCC01, 0xD801, 0x1400, 0xF001, 0x3C00, 0x2800, 0xE401,
                 0xA001, 0x6C00, 0x7800, 0xB401, 0x5000, 0x9C01, 0x8801, 0x4400];

    function crc16(crc, b) {
        var t = CRC_T[crc & 0xF];
        crc = (crc >> 4) & 0x0FFF;
        crc = crc ^ t ^ CRC_T[b & 0xF];
        t = CRC_T[crc & 0xF];
        crc = (crc >> 4) & 0x0FFF;
        return crc ^ t ^ CRC_T[(b >> 4) & 0xF];
    }

    function crcOf(u8, from, to) {
        var c = 0;
        for (var i = from; i < to; i++) c = crc16(c, u8[i]);
        return c;
    }

    // base type id (low 5 bits) → [size, kind]; kind drives reading & the invalid marker
    var BT = {
        0x00: [1, 'enum'], 0x01: [1, 'sint8'], 0x02: [1, 'uint8'], 0x03: [2, 'sint16'],
        0x04: [2, 'uint16'], 0x05: [4, 'sint32'], 0x06: [4, 'uint32'], 0x07: [1, 'string'],
        0x08: [4, 'float32'], 0x09: [8, 'float64'], 0x0A: [1, 'uint8z'], 0x0B: [2, 'uint16z'],
        0x0C: [4, 'uint32z'], 0x0D: [1, 'byte'], 0x0E: [8, 'sint64'], 0x0F: [8, 'uint64'],
        0x10: [8, 'uint64z']
    };

    function readOne(dv, off, kind, le) {
        switch (kind) {
            case 'enum': case 'uint8': case 'byte': { var a = dv.getUint8(off); return a === 0xFF ? undefined : a; }
            case 'uint8z': { var b = dv.getUint8(off); return b === 0 ? undefined : b; }
            case 'sint8': { var c = dv.getInt8(off); return c === 0x7F ? undefined : c; }
            case 'sint16': { var d = dv.getInt16(off, le); return d === 0x7FFF ? undefined : d; }
            case 'uint16': { var e = dv.getUint16(off, le); return e === 0xFFFF ? undefined : e; }
            case 'uint16z': { var f = dv.getUint16(off, le); return f === 0 ? undefined : f; }
            case 'sint32': { var g = dv.getInt32(off, le); return g === 0x7FFFFFFF ? undefined : g; }
            case 'uint32': { var h = dv.getUint32(off, le); return h === 0xFFFFFFFF ? undefined : h; }
            case 'uint32z': { var k = dv.getUint32(off, le); return k === 0 ? undefined : k; }
            case 'float32': { if (dv.getUint32(off, le) === 0xFFFFFFFF) return undefined; return dv.getFloat32(off, le); }
            case 'float64': {
                if (dv.getUint32(off, le) === 0xFFFFFFFF && dv.getUint32(off + 4, le) === 0xFFFFFFFF) return undefined;
                return dv.getFloat64(off, le);
            }
            case 'sint64': case 'uint64': case 'uint64z': {
                // Two 32-bit halves, no BigInt: the site ships ES2019 and these
                // fields (serials, timestamps in ms) fit a double well enough.
                var lo = dv.getUint32(off + (le ? 0 : 4), le), hi = dv.getUint32(off + (le ? 4 : 0), le);
                if (kind === 'sint64' && hi === 0x7FFFFFFF && lo === 0xFFFFFFFF) return undefined;
                if (kind === 'uint64' && hi === 0xFFFFFFFF && lo === 0xFFFFFFFF) return undefined;
                if (kind === 'uint64z' && hi === 0 && lo === 0) return undefined;
                if (kind === 'sint64' && (hi & 0x80000000)) return -((~hi >>> 0) * 4294967296 + (~lo >>> 0) + 1);
                return hi * 4294967296 + lo;
            }
        }
        return undefined;
    }

    var utf8 = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8') : null;

    function readField(u8, dv, off, size, btId, le) {
        var bt = BT[btId & 0x1F];
        if (!bt) return undefined; // unknown base type: skip its bytes
        if (bt[1] === 'string') {
            var end = off;
            while (end < off + size && u8[end] !== 0) end++;
            if (end === off) return undefined;
            return utf8 ? utf8.decode(u8.subarray(off, end)) : String.fromCharCode.apply(null, u8.subarray(off, end));
        }
        var sz = bt[0];
        if (size === sz) return readOne(dv, off, bt[1], le);
        if (size < sz || size % sz) return undefined;
        var arr = [], any = false;
        for (var p = off; p < off + size; p += sz) {
            var v = readOne(dv, p, bt[1], le);
            arr.push(v === undefined ? null : v);
            if (v !== undefined) any = true;
        }
        return any ? arr : undefined;
    }

    function looksLikeHeader(u8, at) {
        if (at + 12 > u8.length) return false;
        var hs = u8[at];
        return (hs === 12 || hs === 14) && u8[at + 8] === 0x2E && u8[at + 9] === 0x46 && u8[at + 10] === 0x49 && u8[at + 11] === 0x54;
    }

    function decode(input) {
        var u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
        var dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
        var out = { messages: [], dev: {}, protocol: null, profile: null, crcOk: true, truncated: false, warnings: [], chained: 0 };
        if (!looksLikeHeader(u8, 0)) throw new Error('not-fit');

        var at = 0;
        while (looksLikeHeader(u8, at)) {
            var hs = u8[at];
            out.protocol = u8[at + 1];
            out.profile = dv.getUint16(at + 2, true);
            var dataSize = dv.getUint32(at + 4, true);
            var start = at + hs;
            var end = start + dataSize;
            if (!dataSize || end > u8.length) {
                // A file still being written (or cut short): read what is there.
                end = u8.length;
                out.truncated = true;
            } else if (end + 2 <= u8.length) {
                if (crcOf(u8, at, end) !== dv.getUint16(end, true)) out.crcOk = false;
            } else {
                out.truncated = true;
            }
            decodeRecords(u8, dv, start, end, out);
            out.chained++;
            at = end + 2;
        }
        if (out.chained > 1) out.warnings.push('chained:' + out.chained);
        return out;
    }

    function decodeRecords(u8, dv, pos, end, out) {
        var defs = {};
        var lastTs = null;
        while (pos < end) {
            var hdr = u8[pos++];
            var local, isDef = false, hasDev = false, compressedOff = -1;
            if (hdr & 0x80) {
                local = (hdr >> 5) & 0x3;
                compressedOff = hdr & 0x1F;
            } else {
                local = hdr & 0x0F;
                isDef = !!(hdr & 0x40);
                hasDev = !!(hdr & 0x20);
            }
            if (isDef) {
                if (pos + 5 > end) { out.truncated = true; return; }
                var le = u8[pos + 1] === 0;
                var g = dv.getUint16(pos + 2, le);
                var nf = u8[pos + 4];
                pos += 5;
                if (pos + nf * 3 > end) { out.truncated = true; return; }
                var fields = [], size = 0;
                for (var i = 0; i < nf; i++) {
                    var fd = { num: u8[pos], size: u8[pos + 1], bt: u8[pos + 2] };
                    fields.push(fd);
                    size += fd.size;
                    pos += 3;
                }
                var devFields = [];
                if (hasDev) {
                    if (pos >= end) { out.truncated = true; return; }
                    var nd = u8[pos++];
                    for (var k = 0; k < nd; k++) {
                        devFields.push({ num: u8[pos], size: u8[pos + 1], idx: u8[pos + 2] });
                        size += u8[pos + 1];
                        pos += 3;
                    }
                }
                defs[local] = { g: g, le: le, fields: fields, dev: devFields, size: size };
                continue;
            }
            var def = defs[local];
            if (!def) {
                // Data before its definition: the rest of this file is unreadable.
                out.warnings.push('orphan-data@' + (pos - 1));
                out.truncated = true;
                return;
            }
            if (pos + def.size > end) { out.truncated = true; return; }
            var msg = { g: def.g, f: {} };
            var p = pos;
            for (var a = 0; a < def.fields.length; a++) {
                var F = def.fields[a];
                var v = readField(u8, dv, p, F.size, F.bt, def.le);
                if (v !== undefined) msg.f[F.num] = v;
                p += F.size;
            }
            if (def.dev.length) {
                msg.d = {};
                for (var b = 0; b < def.dev.length; b++) {
                    var D = def.dev[b];
                    var key = D.idx + ':' + D.num;
                    var desc = out.dev[key];
                    var dvv = desc ? readField(u8, dv, p, D.size, desc.bt, def.le) : undefined;
                    if (dvv !== undefined) msg.d[key] = dvv;
                    p += D.size;
                }
            }
            pos += def.size;

            if (compressedOff >= 0 && lastTs != null) {
                var ts = (lastTs & ~0x1F) + compressedOff;
                if (compressedOff < (lastTs & 0x1F)) ts += 0x20;
                msg.f[253] = ts >>> 0;
            }
            if (msg.f[253] !== undefined && typeof msg.f[253] === 'number') lastTs = msg.f[253];

            if (def.g === 206) {
                // field_description: learn a developer field before its values arrive
                var f = msg.f;
                if (f[0] !== undefined && f[1] !== undefined) {
                    out.dev[f[0] + ':' + f[1]] = {
                        bt: f[2] !== undefined ? f[2] : 0x02,
                        name: f[3] || null,
                        scale: f[6] || 1,
                        offset: f[7] || 0,
                        units: f[8] || null,
                        nativeMesg: f[14],
                        nativeField: f[15]
                    };
                }
            }
            out.messages.push(msg);
        }
    }

    root.GpsFitDecode = {
        decode: decode,
        crc16: crc16,
        crcOf: crcOf,
        isFit: function (u8) { return looksLikeHeader(u8 instanceof Uint8Array ? u8 : new Uint8Array(u8), 0); },
        EPOCH_MS: Date.UTC(1989, 11, 31, 0, 0, 0),
        SEMI_TO_DEG: 180 / 2147483648
    };
})();
